import type { CustomRouteWaypointId } from '@alltrails/types/CustomRoute';
import type { CustomRoutePath, CustomRoutePathStops } from '../../types/CustomRoute';
import { getHandleCount } from './handles';

// A segment's handles sit where its paths meet (see handles.ts). A stop at a handle between two paths is held on both of them,
// as the `to` of the path before it and the `from` of the path after it, so an edit that drops or slices off either path still
// leaves it on the other. Every edit that adds a path next to a handle fills in its side of that handle's stop.

/** The `stops` field of a path with these stops, to spread into it, or nothing when it has none so no empty key is left */
export const stopsField = (from?: CustomRouteWaypointId, to?: CustomRouteWaypointId): { stops?: CustomRoutePathStops } =>
  from === undefined && to === undefined ? {} : { stops: { ...(from !== undefined && { from }), ...(to !== undefined && { to }) } };

/** Sets a path's stops in place, leaving it untouched when they are already these so the path keeps its identity */
export const setPathStops = (path: CustomRoutePath, from?: CustomRouteWaypointId, to?: CustomRouteWaypointId) => {
  if (path.stops?.from === from && path.stops?.to === to) {
    return;
  }
  const { stops } = stopsField(from, to);
  if (stops) {
    path.stops = stops;
  } else {
    delete path.stops;
  }
};

export const getHandleStop = (paths: CustomRoutePath[], handleIndex: number) => paths[handleIndex - 1]?.stops?.to ?? paths[handleIndex]?.stops?.from;

/** The waypoint pinned at each of a segment's handles, or undefined where there is none */
export const getHandleStops = (paths: CustomRoutePath[]) =>
  Array.from({ length: getHandleCount({ paths }) }, (_, index) => getHandleStop(paths, index));

/** Pins a waypoint at a handle, or clears the handle's stop when waypointId is undefined */
export const setHandleStop = (paths: CustomRoutePath[], handleIndex: number, waypointId?: CustomRouteWaypointId) => {
  if (handleIndex < 0 || handleIndex >= getHandleCount({ paths })) {
    return;
  }
  const before = paths[handleIndex - 1];
  if (before) {
    setPathStops(before, before.stops?.from, waypointId);
  }
  const after = paths[handleIndex];
  if (after) {
    setPathStops(after, waypointId, after.stops?.to);
  }
};

/** The way back along paths: in reverse order, each with its points reversed and its stops swapped to match */
export const getReturnPaths = (paths: CustomRoutePath[]): CustomRoutePath[] =>
  [...paths].reverse().map(path => ({ ...path, points: [...path.points].reverse(), ...stopsField(path.stops?.to, path.stops?.from) }));

/** Clears the stops that point at a waypoint the route no longer has, so a waypoint later given a freed id is not taken for one */
export const clearMissingStops = (pathsList: CustomRoutePath[][], waypointIds: CustomRouteWaypointId[]) => {
  const ids = new Set(waypointIds);
  const keep = (waypointId?: CustomRouteWaypointId) => (waypointId !== undefined && ids.has(waypointId) ? waypointId : undefined);
  pathsList.forEach(paths =>
    paths.forEach(path => {
      if (path.stops) {
        setPathStops(path, keep(path.stops.from), keep(path.stops.to));
      }
    })
  );
};

/** The waypoints pinned as stops anywhere on these paths */
export const getStopWaypointIds = (pathsList: CustomRoutePath[][]) =>
  new Set(pathsList.flatMap(paths => paths.flatMap(({ stops }) => [stops?.from, stops?.to])).filter(id => id !== undefined));

/** Unpins a waypoint from every handle it is a stop at */
export const removeWaypointStops = (pathsList: CustomRoutePath[][], waypointId: CustomRouteWaypointId) =>
  pathsList.forEach(paths =>
    paths.forEach(path => {
      if (path.stops) {
        setPathStops(path, path.stops.from === waypointId ? undefined : path.stops.from, path.stops.to === waypointId ? undefined : path.stops.to);
      }
    })
  );
